/**
 * Reason codes the OAuth *start* endpoint can return, mapped to what the user is told.
 *
 * Separate from the callback's codes because the two failures happen at different moments: start
 * fails before the browser ever leaves, and the useful distinction there is "this server can't do
 * OAuth" versus "we couldn't read its configuration". Both used to collapse into one generic red
 * toast, which told the user nothing about whether trying again would help.
 */
export const OAUTH_START_ERROR_MESSAGES = {
  discovery_failed: "mcp_oauth_error_discovery_failed",
  no_registration_endpoint: "mcp_oauth_error_no_registration_endpoint",
  misconfigured: "mcp_oauth_error_misconfigured",
};

/**
 * Resolves the message id to show for a failed OAuth start.
 *
 * The rejected value from httpAPI is the raw `Response` with its body still unread, so the reason
 * code has to be parsed out here. Anything unexpected — a network error rather than a response, a
 * body that isn't JSON, a code this build doesn't know — falls back rather than throwing: a broken
 * error path must not replace a bad message with no message.
 *
 * @param {unknown} err the rejected value from `backend.startMcpOAuth`
 * @param {string} fallback message id to use when no known code is present
 * @returns {Promise<string>} the message id to render
 */
export const oauthStartErrorMessageId = async (err, fallback) => {
  try {
    if (typeof err?.clone === "function") {
      const body = await err.clone().json();
      const messageId = OAUTH_START_ERROR_MESSAGES[body?.code];
      if (messageId) return messageId;
    }
  } catch (ignored) {
    // Not a JSON error response. Fall through to the generic message.
  }
  return fallback;
};
